import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { SvgLoader, SvgProxy } from "react-svgmt";
import { openModal } from "../../store/modal/modalReducer";
import { useDropzone } from "react-dropzone";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import MapLegendManager from "../../components/Admin/MapLegendManager";

// Custom hook for persistent map height
const usePersistentMapHeight = (initialHeight) => {
    const [height, setHeight] = useState(() => {
        const saved = localStorage.getItem("svgMapHeight");
        return saved || initialHeight;
    });

    useEffect(() => {
        localStorage.setItem("svgMapHeight", height);
    }, [height]);

    return [height, setHeight];
};

// Unit Type Color Mapping - Now loaded dynamically from database
// This constant is kept as fallback only
const FALLBACK_UNIT_TYPE_COLORS = {
    Bachelor: "#B3D9FF",
    "Studio Single - Ensuite": "#f09916ec",
    "Single Ensuite in sharing apartment": "#cceec0ff",
    "Single in a Sharing": "#f0ed47ff",
    "Semi Private 2 sharing in an apartment": "#5d7e92b1",
    "4 Sharing Apartment": "#1b8a1bff",
};

export default function Properties3Page() {
    const { property_id, floor_id } = useParams();
    const dispatch = useDispatch();
    const history = useHistory();
    const { current_staff } = useSelector((state) => state.auth);
    const { settings, years } = useSelector((state) => state.settings);

    const [loading, setLoading] = useState(false);
    const [loading_1, setLoading_1] = useState(false);
    const [loading_3, setLoading_3] = useState(false);
    const [loading_4, setLoading_4] = useState(false);
    const [loading_5, setLoading_5] = useState(false);

    const [property, setProperty] = useState({});
    const [floor, setFloor] = useState({});
    const [units, setUnits] = useState([]);
    const [beds, setBeds] = useState([]);
    const [uploading, setUploading] = useState(false);

    const [blocked, setBlocked] = useState("");
    const [booked, setBooked] = useState("");
    const [resident, setResident] = useState("");
    const [waitlisted, setWaitlisted] = useState("");
    const [movedIn, setMovedIn] = useState("");
    const [monthly, setMonthly] = useState("");
    const [perBed, setPerBed] = useState([]);

    const [mappedBeds, setMappedBeds] = useState(false);
    const [allowClick, setAllowClick] = useState(true);
    const [activeYear, setActiveYear] = useState(settings.year_id);
    const filterProp = useRef();
    const [svgMapHeight, setSvgMapHeight] = usePersistentMapHeight("80");
    const [unitGenders, setUnitGenders] = useState({});
    const [isParkingMap, setIsParkingMap] = useState(false);
    const [isHarpersProperty, setIsHarpersProperty] = useState(false);
    const [unitTypeColors, setUnitTypeColors] = useState({});
    const [unitTypeColorsLoading, setUnitTypeColorsLoading] = useState(false);
    const [legendColorMap, setLegendColorMap] = useState({});
    const [showLegendEditor, setShowLegendEditor] = useState(false);
    const [useHarpersCustomView, setUseHarpersCustomView] = useState(false);

    const fetchLegendColors = async (propertyId) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}map-legend-colors/color-map/${propertyId}`);
            console.log("Fetched legend colors from database:", response.data);
            setLegendColorMap(response.data);
        } catch (error) {
            console.error("Failed to fetch legend colors, using fallback:", error);
            setLegendColorMap(FALLBACK_UNIT_TYPE_COLORS);
        }
    };

    const fetchFloorUnitGenders = async (floorId, yearId) => {
        try {
            // Get all beds for the floor with their unit and gender info
            const response = await axios.get(`${process.env.REACT_APP_API_URL}get_floor_beds_gender/${floorId}/${yearId}`);

            // Process the beds to determine unit genders
            const unitGenderMap = {};

            response.data.forEach((bed) => {
                if (!unitGenderMap[bed.unit_id]) {
                    unitGenderMap[bed.unit_id] = {
                        male: 0,
                        female: 0,
                    };
                }

                if (bed.gender === "male") {
                    unitGenderMap[bed.unit_id].male += 1;
                } else if (bed.gender === "female") {
                    unitGenderMap[bed.unit_id].female += 1;
                }
            });

            // Determine final gender for each unit
            const finalUnitGenders = {};
            Object.entries(unitGenderMap).forEach(([unitId, counts]) => {
                if (counts.male > 0 && counts.female > 0) {
                    finalUnitGenders[unitId] = "mixed";
                } else if (counts.male > 0) {
                    finalUnitGenders[unitId] = "male";
                } else if (counts.female > 0) {
                    finalUnitGenders[unitId] = "female";
                } else {
                    finalUnitGenders[unitId] = "default";
                }
            });

            setUnitGenders(finalUnitGenders);
        } catch (error) {
            console.error("Failed to fetch floor unit genders:", error);
        }
    };

    const refreshBookingStates = async (isParking) => {
        console.log("refreshBookingStates called. isParking:", isParking);
        // Reset states first
        setBlocked("");
        setBooked("");
        setResident("");
        setWaitlisted("");
        setMovedIn("");
        setMonthly("");
        setLoading_1(false);
        setLoading_3(false);
        setLoading_4(false);
        setLoading_5(false);

        // UC Logic
        if (process.env.REACT_APP_COLOUR === "UC" && !isParking) {
            try {
                const [bookedRes, residentRes, waitlistedRes, movedInRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}get_floor_bed_bookings/${floor_id}/3/${activeYear}/0`),
                    axios.get(`${process.env.REACT_APP_API_URL}get_floor_bed_bookings/${floor_id}/1/${activeYear}/0`),
                    axios.get(`${process.env.REACT_APP_API_URL}get_floor_bed_bookings/${floor_id}/4/${activeYear}/0`),
                    axios.get(`${process.env.REACT_APP_API_URL}get_floor_bed_bookings/${floor_id}/5/${activeYear}/0`),
                ]);
                setBooked(bookedRes.data);
                setResident(residentRes.data);
                setWaitlisted(waitlistedRes.data);
                setMovedIn(movedInRes.data);
                setLoading_1(true);
                setLoading_3(true);
                setLoading_4(true);
                setLoading_5(true);
            } catch (error) {
                console.error("Failed to refresh UC booking states:", error);
            }
        }
        // QL Logic - Queue Plate colors
        else if (process.env.REACT_APP_COLOUR === "QL" && !isParking) {
            try {
                const statesRes = await axios.get(`${process.env.REACT_APP_API_URL}get_ql_unit_booking_states/${floor_id}/${activeYear}`);
                const states = statesRes.data;
                
                console.log("QL unit booking states:", states);
                
                // Map states to color variables
                setBlocked(states.red || "");       // Red - Staff/Show units
                setMovedIn(states.pink || "");      // Pink - Move-in inspection done
                setWaitlisted(states.purple || ""); // Purple - Part Paid (Renewal Lease Signed)
                setBooked(states.blue || "");       // Blue - Paid & Signed (Lease Signed)
                setResident(states.black || "");    // Black - Lease signed with appointment
                
                setLoading_1(true);
                setLoading_3(true);
                setLoading_4(true);
                setLoading_5(true);
            } catch (error) {
                console.error("Failed to refresh QL booking states:", error);
            }
        }
        // Parking Logic - Pink (addendum signed) and Purple (addendum pending)
        else if (isParking) {
            try {
                console.log("Fetching parking addendum status...");
                // Get parking bays grouped by addendum status (pink/purple)
                const parkingRes = await axios.get(`${process.env.REACT_APP_API_URL}get_floor_parking_addendum_status/${floor_id}`);
                console.log("Parking addendum status response:", parkingRes.data);

                if (parkingRes.data) {
                    // Set pink (addendum signed) and purple (addendum pending)
                    setMovedIn(parkingRes.data.pink || "");      // Pink - addendum signed
                    setWaitlisted(parkingRes.data.purple || ""); // Purple - addendum pending
                    // Vacant bays remain green (default SVG color)
                    
                    console.log("Parking bay colors set:", {
                        pink: parkingRes.data.pink,
                        purple: parkingRes.data.purple
                    });
                }

                setLoading_1(true);
                setLoading_3(true);
            } catch (error) {
                console.error("Failed to refresh parking addendum states:", error);
                // Fallback to old occupied logic
                try {
                    const parkingRes = await axios.get(`${process.env.REACT_APP_API_URL}get_floor_parking_bookings/${floor_id}`);
                    console.log("Fallback parking occupied data:", parkingRes.data);
                    setResident(parkingRes.data);
                    setLoading_1(true);
                    setLoading_3(true);
                } catch (fallbackError) {
                    console.error("Fallback also failed:", fallbackError);
                }
            }
        }
    };

    const updateBed = async (bedData) => {
        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}update_bed`,
                data: bedData,
            });

            setBeds((prevBeds) => {
                return prevBeds.map((bed) => {
                    if (bed.id === bedData.id) {
                        return { ...bed, ...response.data };
                    }
                    return bed;
                });
            });

            // Refresh states using the current parking status
            await refreshBookingStates(isParkingMap);
        } catch (error) {
            console.error("Failed to update bed:", error);
        }
    };

    const deleteMap = async () => {
        if (window.confirm("Are you sure?")) {
            try {
                await axios.delete(`${process.env.REACT_APP_API_URL}delete_map/${floor_id}`);
                setFloor({ ...floor, map_upload: "" });
            } catch (error) {
                console.error("Failed to delete map:", error);
            }
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setIsParkingMap(false); // Reset initially
            setResident(""); // Clear resident state initially
            setIsHarpersProperty(false); // Reset Harper's flag
            setUnitTypeColors({}); // Reset unit type colors

            if (!loading && property_id > 0 && floor_id > 0) {
                try {
                    const [propertyResponse, unitsResponse] = await Promise.all([
                        axios.get(`${process.env.REACT_APP_API_URL}property_floor_info/${property_id}/${floor_id}/${activeYear}`),
                        axios.get(`${process.env.REACT_APP_API_URL}get_floor_units_beds/${floor_id}`),
                    ]);

                    setFloor(propertyResponse.data.floor);
                    setProperty(propertyResponse.data.property);
                    setPerBed(propertyResponse.data.beds_priced);
                    setUnits(unitsResponse.data.units);
                    setBeds(unitsResponse.data.beds);

                    // SAFETY CHECK: Detect Harper's property
                    const isHarpers = parseInt(property_id) === 40;
                    console.log("Property detection:", { property_id, isHarpers });
                    setIsHarpersProperty(isHarpers);

                    // Fetch legend colors from database for this property
                    await fetchLegendColors(property_id);

                    // Fetch unit type colors for Harper's property
                    if (isHarpers) {
                        try {
                            setUnitTypeColorsLoading(true);
                            console.log("Fetching Harper's unit type colors...");

                            const unitTypeResponse = await axios.get(`${process.env.REACT_APP_API_URL}get_floor_bed_unit_types/${floor_id}/${property_id}`);

                            console.log("Harper's unit type colors received:", unitTypeResponse.data);
                            setUnitTypeColors(unitTypeResponse.data || {});
                            setUnitTypeColorsLoading(false);
                        } catch (unitTypeError) {
                            // SAFETY: If unit type fetch fails, log but continue with normal flow
                            console.error("Failed to fetch Harper's unit types, falling back to normal colors:", unitTypeError);
                            setIsHarpersProperty(false); // Disable Harper's mode on error
                            setUnitTypeColorsLoading(false);
                        }
                    }

                    // Check and set isParkingMap using the parking flag
                    console.log("Floor data:", propertyResponse.data.floor);
                    const parkingCheck = propertyResponse.data.floor?.parking === 1;
                    console.log("Setting isParkingMap to:", parkingCheck, "based on floor.parking flag"); // Log check
                    setIsParkingMap(parkingCheck); // Set state here

                    // Refresh states *after* knowing if it's a parking map
                    if (propertyResponse.data.floor.map_upload) {
                        console.log("Refreshing states, isParkingMap:", parkingCheck); // Log check
                        // Pass the determined parking status directly to the function
                        await refreshBookingStates(parkingCheck);
                        // Only fetch gender info if it's not a parking map (optional optimization)
                        if (!parkingCheck) {
                            await fetchFloorUnitGenders(floor_id, activeYear);
                        }
                    }
                } catch (error) {
                    console.error("Failed to fetch data:", error);
                    // SAFETY: Reset Harper's mode on error
                    setIsHarpersProperty(false);
                    setUnitTypeColors({});
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchData();
    }, [mappedBeds, activeYear]); // Keep dependencies, refreshBookingStates is defined outside but called inside

    const onDrop = useCallback(
        async (acceptedFiles) => {
            setUploading(true);
            try {
                await Promise.all(acceptedFiles.map(uploadFile));
                // Refresh states using the current parking status after upload
                await refreshBookingStates(isParkingMap);
            } catch (error) {
                console.error("Upload failed:", error);
            } finally {
                setUploading(false);
            }
        },
        [isParkingMap],
    ); // Add isParkingMap as dependency if refreshBookingStates uses it directly (now passed as arg)

    const uploadFile = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("property_id", property_id);
        formData.append("floor_id", floor_id);

        await axios.post(`${process.env.REACT_APP_API_URL}upload_map`, formData);
    };

    const { getRootProps, getInputProps } = useDropzone({ onDrop });

    return (
        <>
            <div className="h-screen w-auto bg-gray-100 my-6 xl:my-0 xl:mx-4 rounded-3xl flex-1 p-5 overflow-y-auto">
                <div className="flex justify-between">
                    <h1>
                        {property?.name} - {floor?.name}
                    </h1>

                    <select
                        ref={filterProp}
                        className="mt-1 block border font-light border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-primary sm:text-sm transition ease-linear duration-10 hover:border-primary flex-none"
                        onChange={(e) => setActiveYear(e.target.value)}
                    >
                        <option value="0" disabled>
                            Year
                        </option>
                        {years?.map((u) => (
                            <option key={u.id} value={u.id} selected={activeYear === u.id}>
                                {u.year}
                            </option>
                        ))}
                    </select>

                    <div className="max-w-60">
                        <label>Zoom Level (80 = 80% height)</label>
                        <input
                            type="text"
                            name="svg_map_height"
                            value={svgMapHeight}
                            onChange={(e) => setSvgMapHeight(e.target.value)}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        />
                    </div>

                    <div className="space-x-2">
                        <Link
                            className="relative justify-center inline-flex text-white uppercase items-center px-8 py-2 rounded-full border text-sm focus:z-10 focus:outline-none border-primary bg-primary hover:bg-primary_hover focus:ring-primary focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-ease-out hover:-translate-y-0.5 shadow-md font-bold"
                            to="/properties/step1"
                        >
                            Back to Properties
                        </Link>
                        <Link
                            className="relative justify-center inline-flex text-white uppercase items-center px-8 py-2 rounded-full border text-sm focus:z-10 focus:outline-none border-primary bg-primary hover:bg-primary_hover focus:ring-primary focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-ease-out hover:-translate-y-0.5 shadow-md font-bold"
                            to={`/properties/step2/${property_id}`}
                        >
                            Back to Floors
                        </Link>
                    </div>
                </div>

                {/* Add log before render */}
                {console.log("Rendering legend, isParkingMap:", isParkingMap, "isHarpersProperty:", isHarpersProperty)}
                {floor.map_upload && (
                    <div className="space-y-2">
                        {/* Harper's View Toggle - Only show for Harper's property */}
                        {isHarpersProperty && !isParkingMap && (
                            <div className="flex justify-center items-center mb-4">
                                <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm">
                                    <button
                                        onClick={() => setUseHarpersCustomView(false)}
                                        className={`px-6 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                                            !useHarpersCustomView
                                                ? 'bg-blue-600 text-white shadow-sm'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        📊 Regular View
                                    </button>
                                    <button
                                        onClick={() => setUseHarpersCustomView(true)}
                                        className={`px-6 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                                            useHarpersCustomView
                                                ? 'bg-blue-600 text-white shadow-sm'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        🏠 Harper's Custom View
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="flex justify-center items-center space-x-12">
                            {isParkingMap ? (
                                <>
                                    {/* Parking map legend - Pink/Purple/Green for QL */}
                                    {process.env.REACT_APP_COLOUR === "QL" ? (
                                        <>
                                            <div className="flex space-x-2 justify-center items-center">
                                                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "#ec4899" }}></div>
                                                <div className="font-bold">Addendum Signed</div>
                                            </div>
                                            <div className="flex space-x-2 justify-center items-center">
                                                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "#a855f7" }}></div>
                                                <div className="font-bold">Addendum Pending</div>
                                            </div>
                                            <div className="flex space-x-2 justify-center items-center">
                                                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "#90EE90" }}></div>
                                                <div className="font-bold">Vacant</div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex space-x-2 justify-center items-center">
                                                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "blue" }}></div>
                                                <div className="font-bold">{loading_1 === false ? "Loading..." : "Occupied"}</div>
                                            </div>
                                            <div className="flex space-x-2 justify-center items-center">
                                                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "green" }}></div>
                                                <div className="font-bold">{loading_3 === false ? "Loading..." : "Mapped"}</div>
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : isHarpersProperty && useHarpersCustomView && Object.keys(legendColorMap).length > 0 ? (
                                <>
                                    {/* HARPER'S CUSTOM VIEW - Show unit type colors from database */}
                                    {console.log("Rendering Harper's custom unit type legend from database:", legendColorMap)}
                                    {Object.entries(legendColorMap).map(([unitType, color]) => (
                                        <div key={unitType} className="flex space-x-2 justify-center items-center">
                                            <div className="w-5 h-5 rounded-full" style={{ backgroundColor: color }}></div>
                                            <div className="font-bold">{unitType}</div>
                                        </div>
                                    ))}
                                </>
                            ) : process.env.REACT_APP_COLOUR === "QL" ? (
                                <>
                                    {/* QL Queue Plate Colors */}
                                    <div className="flex space-x-2 justify-center items-center">
                                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "#ef4444" }}></div>
                                        <div className="font-bold">Staff/Show Units</div>
                                    </div>
                                    <div className="flex space-x-2 justify-center items-center">
                                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "#ec4899" }}></div>
                                        <div className="font-bold">Move-In Done</div>
                                    </div>
                                    <div className="flex space-x-2 justify-center items-center">
                                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "#a855f7" }}></div>
                                        <div className="font-bold">Part Paid</div>
                                    </div>
                                    <div className="flex space-x-2 justify-center items-center">
                                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "#3b82f6" }}></div>
                                        <div className="font-bold">Paid & Signed</div>
                                    </div>
                                    <div className="flex space-x-2 justify-center items-center">
                                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "#000000" }}></div>
                                        <div className="font-bold">Lease Signed</div>
                                    </div>
                                    <div className="flex space-x-2 justify-center items-center">
                                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "#90EE90" }}></div>
                                        <div className="font-bold">Vacant</div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* UC Colors */}
                                    <div className="flex space-x-2 justify-center items-center">
                                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "black" }}></div>
                                        <div className="font-bold">{loading_1 === false ? "Loading..." : "Reserved / Blocked"}</div>
                                    </div>
                                    <div className="flex space-x-2 justify-center items-center">
                                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "blue" }}></div>
                                        <div className="font-bold">{loading_3 === false ? "Loading..." : "New Leads"}</div>
                                    </div>
                                    <div className="flex space-x-2 justify-center items-center">
                                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "purple" }}></div>
                                        <div className="font-bold">{loading_4 === false ? "Loading..." : "Renewal"}</div>
                                    </div>
                                    <div className="flex space-x-2 justify-center items-center">
                                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "deeppink" }}></div>
                                        <div className="font-bold">{loading_5 === false ? "Loading..." : "Moved In"}</div>
                                    </div>
                                    <div className="flex space-x-2 justify-center items-center">
                                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "green" }}></div>
                                        <div className="font-bold">Mapped</div>
                                    </div>
                                    <div className="flex space-x-2 justify-center items-center">
                                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: "#e5e7eb" }}></div>
                                        <div className="font-bold">Unmapped</div>
                                    </div>
                                </>
                            )}
                        </div>
                        
                        {/* Edit Legend Button - Only show for Harper's custom view */}
                        {isHarpersProperty && useHarpersCustomView && !isParkingMap && (
                            <div className="flex justify-center">
                                <button
                                    onClick={() => setShowLegendEditor(!showLegendEditor)}
                                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition duration-150 ease-in-out shadow-sm"
                                >
                                    {showLegendEditor ? '✕ Close Legend Editor' : '✏️ Edit Legend Colors'}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <div className="map_svg" style={{ height: `${svgMapHeight}vh` }}>
                    {floor.map_upload ? (
                        <>
                            <SvgLoader path={`${process.env.REACT_APP_LARAVEL_URL}api/get_svg/${floor_id}?v=${Math.random()}`} style={{ width: `${svgMapHeight}vw`, height: `${svgMapHeight}vh` }}>
                                {process.env.REACT_APP_COLOUR === "QL" ? <SvgProxy selector=".mapped" fill="green" stroke="black" /> : <SvgProxy selector=".mapped" fill="green" />}
                                {/* Remove specific parking.mapped, handled by resident state below if isParkingMap */}

                                {/* Harper's Property: Selectively hide bedroom labels and square meterage */}
                                {isHarpersProperty && !isParkingMap && (
                                    <>
                                        <SvgProxy
                                            selector="text"
                                            onElementSelected={(elements) => {
                                                Array.from(elements).forEach((textElement) => {
                                                    const textContent = textElement.textContent || "";
                                                    // Hide if contains square meterage (m², m2, sqm), "bedroom", or "bath"
                                                    if (
                                                        textContent.includes("m²") ||
                                                        textContent.includes("m2") ||
                                                        textContent.includes("sqm") ||
                                                        textContent.toLowerCase().includes("bedroom") ||
                                                        textContent.toLowerCase().includes("bath")
                                                    ) {
                                                        // Hide text element
                                                        textElement.style.display = "none";

                                                        // Get text bounding box to find overlapping rectangles
                                                        try {
                                                            const textBox = textElement.getBBox();

                                                            // Search for white rectangles in the entire SVG
                                                            const svg = textElement.ownerSVGElement;
                                                            if (svg) {
                                                                const allRects = svg.querySelectorAll("rect");
                                                                Array.from(allRects).forEach((rect) => {
                                                                    // Check if rect has white fill
                                                                    const fill = rect.getAttribute("fill") || rect.style.fill || window.getComputedStyle(rect).fill;

                                                                    if (
                                                                        fill === "white" ||
                                                                        fill === "#fff" ||
                                                                        fill === "#ffffff" ||
                                                                        fill === "rgb(255, 255, 255)" ||
                                                                        (typeof fill === "string" && fill.toLowerCase().includes("white"))
                                                                    ) {
                                                                        // Get rect bounding box
                                                                        try {
                                                                            const rectBox = rect.getBBox();
                                                                            // Check if rect overlaps with text (within reasonable proximity)
                                                                            const overlaps = !(
                                                                                rectBox.x + rectBox.width < textBox.x - 5 ||
                                                                                rectBox.x > textBox.x + textBox.width + 5 ||
                                                                                rectBox.y + rectBox.height < textBox.y - 5 ||
                                                                                rectBox.y > textBox.y + textBox.height + 5
                                                                            );

                                                                            if (overlaps) {
                                                                                rect.style.display = "none";
                                                                            }
                                                                        } catch (e) {
                                                                            // Skip if getBBox fails
                                                                        }
                                                                    }
                                                                });
                                                            }
                                                        } catch (e) {
                                                            // If getBBox fails on text, just hide the text
                                                        }
                                                    }
                                                });
                                            }}
                                        />
                                    </>
                                )}

                                <SvgProxy
                                    selector=".room > g > rect.room_inner.highlighted, .room > g > path.room_inner.highlighted, .room
                                    > g > rect.room_container.highlighted, .room > g > path.room_container.highlighted"
                                    fill="#2ceaeb"
                                />
                                <SvgProxy selector=".taken" fill="#ff0000" />
                                <SvgProxy selector=".resident_reserved" fill={process.env.REACT_APP_COLOUR === "UC" ? "black" : "#4a4a4a"} />
                                <SvgProxy selector=".waitlist" fill="purple" />

                                <SvgProxy selector="rect:hover, path:hover" fill="orange" />

                                <SvgProxy
                                    selector="rect, path"
                                    onClick={(e) => {
                                        const currentTarget = e.currentTarget;

                                        if (isParkingMap) {
                                            let targetElement = currentTarget;
                                            let parkingBayId = null;

                                            // Check if the current element or its parent is already mapped
                                            if (currentTarget.hasAttribute("parking_bay_id")) {
                                                parkingBayId = currentTarget.getAttribute("parking_bay_id");
                                                // targetElement remains currentTarget
                                            } else if (currentTarget.parentElement && currentTarget.parentElement.hasAttribute("parking_bay_id")) {
                                                targetElement = currentTarget.parentElement; // Use the parent if it's mapped
                                                parkingBayId = targetElement.getAttribute("parking_bay_id");
                                            }

                                            // Always open the modal for parking maps
                                            dispatch(
                                                openModal({
                                                    modal_type: "ModalParkingBay",
                                                    modal_props: {
                                                        property_id,
                                                        floor_id,
                                                        thisbed: targetElement, // Pass the mapped element OR the clicked element
                                                        setMappedBeds,
                                                        mappedBeds,
                                                        activeYear,
                                                        isParkingMap,
                                                        current_booking: {
                                                            id: property_id, // Pass the property ID as booking ID
                                                            property_id: property_id,
                                                            user: {
                                                                id: current_staff?.id, // Pass the current staff ID as user ID
                                                                name: current_staff?.name,
                                                                surname: current_staff?.surname,
                                                            },
                                                        },
                                                        current_staff,
                                                    },
                                                }),
                                            );
                                        } else {
                                            // Original logic for non-parking maps (beds/units)
                                            dispatch(
                                                openModal({
                                                    modal_type: process.env.REACT_APP_COLOUR === "UC" ? "ModalPropBed" : "ModalPropUnit",
                                                    modal_props: {
                                                        property_id,
                                                        floor_id,
                                                        thisbed: currentTarget,
                                                        setMappedBeds,
                                                        mappedBeds,
                                                        activeYear,
                                                        updateBed,
                                                        isParkingMap,
                                                    },
                                                }),
                                            );
                                        }
                                    }}
                                    onMouseEnter={(e) => {
                                        const currentTarget = e.currentTarget;
                                        let targetElement = null;
                                        let parkingBayId = null;
                                        let bayNumber = null;
                                        let bedId = null;

                                        if (isParkingMap) {
                                            // Check current element for parking info
                                            if (currentTarget.hasAttribute("parking_bay_id") && currentTarget.hasAttribute("bay_number")) {
                                                targetElement = currentTarget;
                                            }
                                            // Check parent element for parking info
                                            else if (
                                                currentTarget.parentElement &&
                                                currentTarget.parentElement.hasAttribute("parking_bay_id") &&
                                                currentTarget.parentElement.hasAttribute("bay_number")
                                            ) {
                                                targetElement = currentTarget.parentElement;
                                            }

                                            if (targetElement) {
                                                parkingBayId = targetElement.getAttribute("parking_bay_id");
                                                bayNumber = targetElement.getAttribute("bay_number");
                                                targetElement.setAttribute("data-tooltip-id", "bed-tooltip"); // Re-use tooltip ID or create a new one
                                                targetElement.setAttribute("data-tooltip-content", `Parking Bay: ${bayNumber}`);
                                            }
                                        } else {
                                            // Original logic for bed tooltips
                                            bedId = currentTarget.getAttribute("bedid"); // Check current target first
                                            if (!bedId && currentTarget.parentElement) {
                                                bedId = currentTarget.parentElement.getAttribute("bedid"); // Check parent if needed
                                            }

                                            if (bedId) {
                                                const bed = beds.find((b) => b.id === parseInt(bedId));
                                                if (bed) {
                                                    const tooltipElement =
                                                        currentTarget.parentElement && currentTarget.parentElement.hasAttribute("bedid") ? currentTarget.parentElement : currentTarget;
                                                    tooltipElement.setAttribute("data-tooltip-id", "bed-tooltip");
                                                    tooltipElement.setAttribute("data-tooltip-content", `Unit Type: ${bed.unit_type?.name}\nBed: ${bed.unit?.name}${bed.name}\nPrice: R${bed.price}`);
                                                }
                                            }
                                        }
                                    }}
                                    // Add onMouseLeave to potentially clear the tooltip attributes if needed
                                    // onMouseLeave={(e) => {
                                    //     e.currentTarget.removeAttribute("data-tooltip-id");
                                    //     e.currentTarget.removeAttribute("data-tooltip-content");
                                    //     if (e.currentTarget.parentElement) {
                                    //       e.currentTarget.parentElement.removeAttribute("data-tooltip-id");
                                    //       e.currentTarget.parentElement.removeAttribute("data-tooltip-content");
                                    //     }
                                    // }}
                                />

                                <SvgProxy selector=".blocked" fill="red" />
                                <SvgProxy selector=".blocked.yellow" fill="yellow" />

                                {booked != "" && (
                                    <SvgProxy
                                        selector={booked}
                                        fill="blue"
                                        onClick={(e) => {
                                            dispatch(
                                                openModal({
                                                    modal_type: isParkingMap ? "ModalParkingBay" : process.env.REACT_APP_COLOUR === "UC" ? "ModalPropBed" : "ModalPropUnit",
                                                    modal_props: {
                                                        property_id: property_id,
                                                        floor_id: floor_id,
                                                        thisbed: e.currentTarget,
                                                        setMappedBeds: setMappedBeds,
                                                        mappedBeds: mappedBeds,
                                                        activeYear: activeYear,
                                                        isParkingMap,
                                                    },
                                                }),
                                            );
                                        }}
                                    />
                                )}

                                {/* UC = waitlisted | QL = 2nd Last month of lease*/}
                                {waitlisted != "" && (
                                    <SvgProxy
                                        selector={waitlisted}
                                        fill="purple"
                                        onClick={(e) => {
                                            dispatch(
                                                openModal({
                                                    modal_type: isParkingMap ? "ModalParkingBay" : process.env.REACT_APP_COLOUR === "UC" ? "ModalPropBed" : "ModalPropUnit",
                                                    modal_props: {
                                                        property_id: property_id,
                                                        floor_id: floor_id,
                                                        thisbed: e.currentTarget,
                                                        setMappedBeds: setMappedBeds,
                                                        mappedBeds: mappedBeds,
                                                        activeYear: activeYear,
                                                        isParkingMap,
                                                    },
                                                }),
                                            );
                                        }}
                                    />
                                )}

                                {/* UC = resident | QL = Occupied (non-parking only) */}
                                {resident != "" && !isParkingMap && (
                                    <>
                                        {console.log("Applying resident selectors:", resident)}
                                        <SvgProxy
                                            selector={resident}
                                            fill={process.env.REACT_APP_COLOUR === "UC" ? "black" : "#4a4a4a"}
                                            onClick={(e) => {
                                                dispatch(
                                                    openModal({
                                                        modal_type: process.env.REACT_APP_COLOUR === "UC" ? "ModalPropBed" : "ModalPropUnit",
                                                        modal_props: {
                                                            property_id: property_id,
                                                            floor_id: floor_id,
                                                            thisbed: e.currentTarget,
                                                            setMappedBeds: setMappedBeds,
                                                            mappedBeds: mappedBeds,
                                                            activeYear: activeYear,
                                                            isParkingMap,
                                                        },
                                                    }),
                                                );
                                            }}
                                        />
                                    </>
                                )}

                                {/* Parking map colors - Pink (addendum signed) and Purple (addendum pending) */}
                                {isParkingMap && process.env.REACT_APP_COLOUR === "QL" && (
                                    <>
                                        {movedIn !== "" && <SvgProxy selector={movedIn} fill="#ec4899" onClick={(e) => {
                                            dispatch(openModal({
                                                modal_type: "ModalParkingBay",
                                                modal_props: { property_id, floor_id, thisbed: e.currentTarget, setMappedBeds, mappedBeds, activeYear, isParkingMap }
                                            }));
                                        }} />}
                                        {waitlisted !== "" && <SvgProxy selector={waitlisted} fill="#a855f7" onClick={(e) => {
                                            dispatch(openModal({
                                                modal_type: "ModalParkingBay",
                                                modal_props: { property_id, floor_id, thisbed: e.currentTarget, setMappedBeds, mappedBeds, activeYear, isParkingMap }
                                            }));
                                        }} />}
                                    </>
                                )}

                                {/* QL Queue Plate Colors - Only show if not parking map */}
                                {!isParkingMap && process.env.REACT_APP_COLOUR === "QL" && (
                                    <>
                                        {blocked !== "" && <SvgProxy selector={blocked} fill="#ef4444" onClick={(e) => {
                                            const isBed = e.currentTarget.hasAttribute("bedid");
                                            dispatch(openModal({
                                                modal_type: isBed ? "ModalPropBed" : "ModalPropUnit",
                                                modal_props: { property_id, floor_id, thisbed: e.currentTarget, setMappedBeds, mappedBeds, activeYear, isParkingMap }
                                            }));
                                        }} />}
                                        {movedIn !== "" && <SvgProxy selector={movedIn} fill="#ec4899" onClick={(e) => {
                                            const isBed = e.currentTarget.hasAttribute("bedid");
                                            dispatch(openModal({
                                                modal_type: isBed ? "ModalPropBed" : "ModalPropUnit",
                                                modal_props: { property_id, floor_id, thisbed: e.currentTarget, setMappedBeds, mappedBeds, activeYear, isParkingMap }
                                            }));
                                        }} />}
                                        {waitlisted !== "" && <SvgProxy selector={waitlisted} fill="#a855f7" onClick={(e) => {
                                            const isBed = e.currentTarget.hasAttribute("bedid");
                                            dispatch(openModal({
                                                modal_type: isBed ? "ModalPropBed" : "ModalPropUnit",
                                                modal_props: { property_id, floor_id, thisbed: e.currentTarget, setMappedBeds, mappedBeds, activeYear, isParkingMap }
                                            }));
                                        }} />}
                                        {booked !== "" && <SvgProxy selector={booked} fill="#3b82f6" onClick={(e) => {
                                            const isBed = e.currentTarget.hasAttribute("bedid");
                                            dispatch(openModal({
                                                modal_type: isBed ? "ModalPropBed" : "ModalPropUnit",
                                                modal_props: { property_id, floor_id, thisbed: e.currentTarget, setMappedBeds, mappedBeds, activeYear, isParkingMap }
                                            }));
                                        }} />}
                                        {resident !== "" && <SvgProxy selector={resident} fill="#4a4a4a" onClick={(e) => {
                                            const isBed = e.currentTarget.hasAttribute("bedid");
                                            dispatch(openModal({
                                                modal_type: isBed ? "ModalPropBed" : "ModalPropUnit",
                                                modal_props: { property_id, floor_id, thisbed: e.currentTarget, setMappedBeds, mappedBeds, activeYear, isParkingMap }
                                            }));
                                        }} />}
                                    </>
                                )}

                                {/* UC Colors - Only show if not parking map */}
                                {!isParkingMap && process.env.REACT_APP_COLOUR === "UC" && (
                                    <>
                                        {movedIn !== "" && <SvgProxy selector={movedIn} fill="deeppink" />}
                                        {monthly !== "" && <SvgProxy selector={monthly} fill="#757575" />}
                                    </>
                                )}

                                {/* UNIT TYPE COLORS - Dynamically loaded from database */}
                                {isHarpersProperty && !isParkingMap && Object.keys(unitTypeColors).length > 0 && Object.keys(legendColorMap).length > 0 && (
                                    <>
                                        {console.log("Applying unit type colors from database:", legendColorMap)}
                                        {Object.entries(unitTypeColors).map(([unitType, selectors]) => {
                                            const color = legendColorMap[unitType];
                                            if (color && selectors) {
                                                console.log(`Applying color ${color} to unit type: ${unitType}, selectors: ${selectors}`);
                                                return (
                                                    <SvgProxy
                                                        key={`unit-type-${unitType}`}
                                                        selector={selectors}
                                                        fill={color}
                                                        onClick={(e) => {
                                                            dispatch(
                                                                openModal({
                                                                    modal_type: "ModalPropBed",
                                                                    modal_props: {
                                                                        property_id,
                                                                        floor_id,
                                                                        thisbed: e.currentTarget,
                                                                        setMappedBeds,
                                                                        mappedBeds,
                                                                        activeYear,
                                                                        updateBed,
                                                                        isParkingMap,
                                                                    },
                                                                }),
                                                            );
                                                        }}
                                                    />
                                                );
                                            }
                                            return null;
                                        })}
                                    </>
                                )}

                                <SvgProxy
                                    selector=".room"
                                    onElementSelected={(elements) => {
                                        Array.from(elements).forEach((element) => {
                                            if (element.classList.contains("room")) {
                                                const unitId = element.getAttribute("unitid");
                                                const gender = unitGenders[unitId];

                                                // Define colors object for better maintenance
                                                const colors = {
                                                    male: {
                                                        fill: "rgb(121,169,242)",
                                                        stroke: "#4A75B8",
                                                    },
                                                    female: {
                                                        fill: "rgb(242,121,151)",
                                                        stroke: "#B84A6E",
                                                    },
                                                    mixed: {
                                                        fill: "#FFA500", // Orange for mixed gender
                                                        stroke: "#CC8400", // Darker orange for stroke
                                                    },
                                                    default: {
                                                        fill: "#E0E0E0",
                                                        stroke: "#A0A0A0",
                                                    },
                                                };

                                                // Get the appropriate colors based on gender
                                                const colorSet = colors[gender] || colors.default;

                                                // Apply the colors
                                                element.style.fill = colorSet.fill;
                                                element.style.stroke = colorSet.stroke;

                                                // Add a warning indicator for mixed units (optional)
                                                if (gender === "mixed") {
                                                    // You could add additional visual indicators here if needed
                                                    element.style.strokeWidth = "2"; // Make the stroke thicker for mixed units
                                                }
                                            }
                                        });
                                    }}
                                />
                            </SvgLoader>
                            <Tooltip
                                id="bed-tooltip"
                                place="top"
                                className="z-50"
                                style={{
                                    backgroundColor: "#333",
                                    color: "white",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    whiteSpace: "pre-line", // This will respect the \n line breaks
                                }}
                            />
                        </>
                    ) : (
                        <div className="bg-light_background mt-1 sm:mt-0 sm:col-span-2" {...getRootProps()}>
                            <div className="max-w-lg flex justify-center px-6 pt-5 pb-6 border-2 border-dark_background border-dashed rounded-md">
                                <div className="space-y-1 text-center">
                                    {!uploading ? (
                                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                                            <path
                                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                                strokeWidth={2}
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    ) : (
                                        <div>Uploading...</div>
                                    )}
                                    <div className="flex text-sm text-gray-600">
                                        <label className="relative cursor-pointer bg-light_background rounded-md font-medium text-primary hover:text-primary_hover focus-within:outline-none">
                                            {!uploading ? "Drop your files here" : "Files uploading"}
                                            <input {...getInputProps()} />
                                        </label>
                                    </div>
                                    {!uploading && <p className="text-xs text-gray-500">up to 10MB per file</p>}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-2 mt-4">
                    <div>
                        {perBed?.length > 0 && (
                            <>
                                <p className="text-xl font-bold">Priced per Bed:</p>
                                <ul className="mt-2">
                                    {perBed.map((bed) => (
                                        <li key={`${bed.unitName}-${bed.bedName}`}>
                                            {bed.unitName} - {bed.bedName}
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>

                    <div>
                        <div>Beds Count = {beds.length}</div>
                        <div>Blocked Red Count = {beds.filter((b) => b.bed_status === 1)?.length}</div>
                        <div>Blocked Yellow Count = {beds.filter((b) => b.bed_status === 2)?.length}</div>
                        <div>Pink Count = {movedIn?.split(",")?.length}</div>
                        <div>Black Count = {resident?.split(",")?.length - movedIn?.split(",")?.length}</div>
                    </div>
                </div>

                {/* Legend Editor Modal - Only for Harper's Custom View */}
                {showLegendEditor && isHarpersProperty && useHarpersCustomView && (
                    <div className="mt-6 bg-white rounded-lg shadow-lg p-6 border-2 border-blue-500">
                        <div className="mb-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-gray-800">Edit Harper's Custom Unit Type Colors</h2>
                            <button
                                onClick={() => setShowLegendEditor(false)}
                                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                            >
                                ✕
                            </button>
                        </div>
                        
                        <MapLegendManager 
                            propertyId={property_id} 
                            propertyName={property?.name}
                            onColorsUpdated={() => {
                                // Refresh colors when changes are made
                                fetchLegendColors(property_id);
                            }}
                        />
                        
                        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                            <p className="text-sm text-yellow-800">
                                <strong>💡 Tip:</strong> After making changes, refresh this page to see the updated colors applied to the map.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
